import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, ShoppingBag, Clock, Package, Search as SearchIcon, Plus, Trash2, Globe2, Crown, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/AppContext";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function AdminPanel() {
  const { t, lang, toggleLang } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [logs, setLogs] = useState([]);
  const [visits, setVisits] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ pn: "", name: "", category: "drone", price: "", quantity: "", location: "", condition: "New" });

  const loadAll = async () => {
    try {
      const [s, o, st, l, v, m] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/orders"),
        api.get("/admin/stock"), api.get("/admin/search-logs"),
        api.get("/admin/visits/stats"), api.get("/admin/membership-requests"),
      ]);
      setStats(s.data); setOrders(o.data); setStock(st.data); setLogs(l.data); setVisits(v.data); setMembers(m.data);
    } catch {
      toast.error("Load error");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const activateMember = async (id) => {
    try {
      await api.post(`/admin/membership-requests/${id}/approve`);
      toast.success(t("member_activated"));
      loadAll();
    } catch { toast.error("Error"); }
  };

  const deactivateMember = async (id) => {
    try {
      await api.post(`/admin/membership-requests/${id}/deactivate`);
      toast.success(t("member_deactivated"));
      loadAll();
    } catch { toast.error("Error"); }
  };

  const addStock = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stock", { ...form, price: parseFloat(form.price) || 0, quantity: parseInt(form.quantity) || 0 });
      toast.success("Added");
      setForm({ pn: "", name: "", category: "drone", price: "", quantity: "", location: "", condition: "New" });
      loadAll();
    } catch { toast.error("Error"); }
  };

  const delStock = async (id) => { await api.delete(`/admin/stock/${id}`); loadAll(); };

  const flag = (cc) => {
    if (!cc || cc.length !== 2 || cc === "XX") return "🏳️";
    return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
  };

  const updateOrder = async (id, status) => {
    await api.put(`/admin/orders/${id}`, { status });
    loadAll();
    toast.success("Updated");
  };

  const statCards = stats ? [
    { icon: Users, label: t("total_users"), value: stats.users },
    { icon: ShoppingBag, label: t("total_orders"), value: stats.orders },
    { icon: Clock, label: t("pending"), value: stats.pending_orders },
    { icon: Package, label: t("total_stock"), value: stats.stock_items },
    { icon: SearchIcon, label: t("total_searches"), value: stats.searches },
  ] : [];

  return (
    <div className="min-h-screen ps-grid-bg">
      <header className="sticky top-0 z-40 border-b border-cyan-500/20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <button data-testid="admin-back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-600 hover:text-cyan-700">
            <ArrowLeft size={18} /> <span className="font-head font-bold">{t("admin_panel")}</span>
          </button>
          <button data-testid="admin-lang-btn" onClick={toggleLang} className="font-mono-ps text-xs border border-cyan-500/40 rounded-full px-3 py-1 text-cyan-700">
            {lang === "TR" ? "EN" : "TR"}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {statCards.map((c, i) => (
            <div key={i} className="ps-glow-card rounded-xl p-4" data-testid={`stat-${i}`}>
              <c.icon className="text-cyan-600 mb-2" size={20} />
              <p className="font-display text-2xl font-black text-slate-900">{c.value}</p>
              <p className="text-xs text-slate-500">{c.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="orders">
          <TabsList className="bg-slate-50 border border-slate-200">
            <TabsTrigger value="orders" data-testid="admin-tab-orders">{t("order_mgmt")}</TabsTrigger>
            <TabsTrigger value="memberships" data-testid="admin-tab-memberships">
              {t("memberships_tab")}
              {members.filter((m) => m.status !== "approved").length > 0 && (
                <span data-testid="pending-memberships-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {members.filter((m) => m.status !== "approved").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="stock" data-testid="admin-tab-stock">{t("stock_mgmt")}</TabsTrigger>
            <TabsTrigger value="logs" data-testid="admin-tab-logs">{t("search_logs")}</TabsTrigger>
            <TabsTrigger value="visits" data-testid="admin-tab-visits">{t("visits_tab")}</TabsTrigger>
          </TabsList>

          {/* Orders */}
          <TabsContent value="orders" className="mt-4">
            <div className="ps-glow-card rounded-xl p-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200">
                    <TableHead>ID</TableHead><TableHead>{t("email")}</TableHead>
                    <TableHead>{t("total")}</TableHead><TableHead>{t("status")}</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o, i) => (
                    <TableRow key={o.id} className="border-slate-200" data-testid={`admin-order-${i}`}>
                      <TableCell className="font-mono-ps text-xs">#{o.id.slice(-6)}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{o.user_email}</TableCell>
                      <TableCell className="font-bold text-cyan-600">${Number(o.total).toLocaleString()}</TableCell>
                      <TableCell>
                        <Select value={o.status} onValueChange={(v) => updateOrder(o.id, v)}>
                          <SelectTrigger data-testid={`order-status-${i}`} className="w-32 h-8 bg-slate-50 border-slate-300 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">pending</SelectItem>
                            <SelectItem value="shipped">shipped</SelectItem>
                            <SelectItem value="completed">completed</SelectItem>
                            <SelectItem value="cancelled">cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  ))}
                  {orders.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">—</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Memberships */}
          <TabsContent value="memberships" className="mt-4">
            <div className="ps-glow-card rounded-xl p-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200">
                    <TableHead>{t("email")}</TableHead><TableHead>{t("name")}</TableHead>
                    <TableHead>{t("plan")}</TableHead><TableHead>{t("status")}</TableHead>
                    <TableHead>{t("requested_at")}</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m, i) => (
                    <TableRow key={m.id} className="border-slate-200" data-testid={`admin-member-${i}`}>
                      <TableCell className="text-slate-700 text-sm">{m.email}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{m.name || "—"}</TableCell>
                      <TableCell className="text-amber-600 text-sm font-bold flex items-center gap-1"><Crown size={14} /> Buyer/Seller</TableCell>
                      <TableCell>
                        {m.status === "approved" ? (
                          <div className="flex flex-col">
                            <span className="text-emerald-600 text-xs font-mono-ps font-bold">{t("status_approved")}</span>
                            {m.expires_at && <span className="text-[10px] text-slate-400 font-mono-ps">{t("valid_until")}: {m.expires_at.slice(0, 10)}</span>}
                          </div>
                        ) : (
                          <span className="text-amber-600 text-xs font-mono-ps">{t("status_pending")}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs font-mono-ps">{(m.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell>
                        {m.status === "approved" ? (
                          <Button data-testid={`member-deactivate-${i}`} onClick={() => deactivateMember(m.id)}
                            className="h-8 bg-slate-100 hover:bg-red-50 text-red-500 border border-red-200 text-xs">
                            <XCircle size={14} className="mr-1" /> {t("member_deactivate")}
                          </Button>
                        ) : (
                          <Button data-testid={`member-activate-${i}`} onClick={() => activateMember(m.id)}
                            className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white text-xs">
                            <Check size={14} className="mr-1" /> {t("member_activate")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {members.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">{t("no_requests")}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Stock */}
          <TabsContent value="stock" className="mt-4 space-y-4">
            <form onSubmit={addStock} className="ps-glow-card rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stock-form">
              <Input data-testid="stock-pn" placeholder={t("pn")} value={form.pn} onChange={(e) => setForm({ ...form, pn: e.target.value })} className="bg-slate-50 border-slate-300" required />
              <Input data-testid="stock-name" placeholder={t("part_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-50 border-slate-300" required />
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="stock-category" className="bg-slate-50 border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="drone">{t("drone")}</SelectItem>
                  <SelectItem value="aircraft">{t("aircraft")}</SelectItem>
                  <SelectItem value="machinery">{t("machinery")}</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="stock-price" type="number" placeholder={t("price")} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-slate-50 border-slate-300" />
              <Input data-testid="stock-qty" type="number" placeholder={t("quantity")} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-slate-50 border-slate-300" />
              <Input data-testid="stock-location" placeholder={t("location")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-slate-50 border-slate-300" />
              <Button data-testid="stock-add-btn" type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold col-span-2 md:col-span-1">
                <Plus size={16} className="mr-1" /> {t("add_part")}
              </Button>
            </form>

            <div className="ps-glow-card rounded-xl p-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200">
                    <TableHead>{t("pn")}</TableHead><TableHead>{t("part_name")}</TableHead>
                    <TableHead>{t("category")}</TableHead><TableHead>{t("price")}</TableHead>
                    <TableHead>{t("stock")}</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stock.map((s, i) => (
                    <TableRow key={s.id} className="border-slate-200" data-testid={`admin-stock-${i}`}>
                      <TableCell className="font-mono-ps text-cyan-700 text-xs">{s.pn}</TableCell>
                      <TableCell className="text-slate-700 text-sm">{s.name}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{s.category}</TableCell>
                      <TableCell className="text-slate-900">${Number(s.price).toLocaleString()}</TableCell>
                      <TableCell className={s.quantity > 0 ? "text-emerald-600" : "text-amber-500"}>{s.quantity}</TableCell>
                      <TableCell>
                        <button data-testid={`stock-delete-${i}`} onClick={() => delStock(s.id)} className="text-red-400 hover:text-red-300"><Trash2 size={15} /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Logs */}
          <TabsContent value="logs" className="mt-4">
            <div className="ps-glow-card rounded-xl p-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200">
                    <TableHead>{t("query")}</TableHead><TableHead>{t("category")}</TableHead>
                    <TableHead>{t("email")}</TableHead><TableHead>{t("results")}</TableHead><TableHead>MilSis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l, i) => (
                    <TableRow key={l.id} className="border-slate-200" data-testid={`admin-log-${i}`}>
                      <TableCell className="text-slate-700 text-sm">{l.query}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{l.category}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{l.user_email}</TableCell>
                      <TableCell className="text-cyan-600">{l.results_count}</TableCell>
                      <TableCell className="text-emerald-600">{l.internal_count}</TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">—</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Visits */}
          <TabsContent value="visits" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <div className="ps-glow-card rounded-xl p-4" data-testid="visit-total">
                <Globe2 className="text-cyan-600 mb-2" size={20} />
                <p className="font-display text-2xl font-black text-slate-900">{visits?.total_visits ?? 0}</p>
                <p className="text-xs text-slate-500">{t("visitors")}</p>
              </div>
              <div className="ps-glow-card rounded-xl p-4" data-testid="visit-unique">
                <Users className="text-cyan-600 mb-2" size={20} />
                <p className="font-display text-2xl font-black text-slate-900">{visits?.unique_visitors ?? 0}</p>
                <p className="text-xs text-slate-500">{t("unique_visitors")}</p>
              </div>
            </div>
            <div className="ps-glow-card rounded-xl p-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-200">
                    <TableHead>{t("country")}</TableHead><TableHead>{t("visits_count")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(visits?.by_country || []).map((c, i) => (
                    <TableRow key={i} className="border-slate-200" data-testid={`visit-country-${i}`}>
                      <TableCell className="text-slate-700 text-sm">{flag(c.country)} {c.country}</TableCell>
                      <TableCell className="text-cyan-600">{c.count}</TableCell>
                    </TableRow>
                  ))}
                  {(!visits || (visits.by_country || []).length === 0) && (
                    <TableRow><TableCell colSpan={2} className="text-center text-slate-500 py-6">—</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
